import { useEffect, useState, useCallback, useMemo } from 'react';
import api, { TIMEOUT_UPLOAD } from '../services/api';
import toast from 'react-hot-toast';
import { UserPlus, Pencil, Trash2, X, Check, Users, ChevronDown, ChevronUp, Plus, Zap, Bot, BotOff, Upload } from 'lucide-react';
import { SkeletonTableRows } from '../components/Skeletons';
import Tooltip from '../components/Tooltip';
import ajuda from '../utils/ajuda';
import { useT } from '../i18n';

function InstanciaBadge({ nome }) {
  if (!nome) return null;
  return (
    <span style={{
      fontSize: '11px', background: '#dcfce7', color: '#13305f',
      borderRadius: '4px', padding: '2px 6px', whiteSpace: 'nowrap', display: 'inline-block'
    }}>
      {nome}
    </span>
  );
}

export default function Contatos() {
  const { t } = useT();
  const [contatos, setContatos] = useState([]);
  const [form, setForm] = useState({ nome: '', telefone: '', email: '', tags: '' });
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEdit, setFormEdit] = useState({ nome: '', telefone: '', email: '', tags: '' });
  const [filtroTag, setFiltroTag] = useState('');
  const [selecionados, setSelecionados] = useState(new Set());
  const [excluindoEmMassa, setExcluindoEmMassa] = useState(false);
  const [primeiraCarga, setPrimeiraCarga] = useState(true);
  const [deduplicando, setDeduplicando] = useState(false);
  const [importando, setImportando] = useState(false);
  // Instância do CSV: OPCIONAL e só organizacional — quem decide por qual número a
  // mensagem sai é a CAMPANHA. Vazio = importa sem vínculo (como era antes).
  const [instanciaImport, setInstanciaImport] = useState('');

  // Instâncias registradas no WhatsApp — usado apenas no modal/formulários
  const [instanciasRegistradas, setInstanciasRegistradas] = useState([]);

  // Filtro unificado
  const [filtroInstancia, setFiltroInstancia] = useState('');

  // Grupos
  const [grupos, setGrupos] = useState([]);
  const [gruposAbertos, setGruposAbertos] = useState(true);
  const [mostrarModalGrupo, setMostrarModalGrupo] = useState(false);
  const [formGrupo, setFormGrupo] = useState({ nome: '', descricao: '', instancia: '' });
  const [criandoGrupo, setCriandoGrupo] = useState(false);
  const [mostrarAutoGrupo, setMostrarAutoGrupo] = useState(false);
  const [autoForm, setAutoForm] = useState({ tamanho: 20, prefixo: 'Grupo', instancia: '' });
  const [gerandoAuto, setGerandoAuto] = useState(false);
  // Seleção de contatos dentro do modal de grupo
  const [modalContatosBusca, setModalContatosBusca] = useState('');
  const [modalContatosSel, setModalContatosSel] = useState(new Set());
  // Edição de contatos de grupo existente
  const [grupoEditando, setGrupoEditando] = useState(null);
  const [grupoEditandoContatos, setGrupoEditandoContatos] = useState(new Set());
  const [salvandoGrupoEdit, setSalvandoGrupoEdit] = useState(false);

  // Deriva instâncias únicas diretamente dos contatos (já scoped ao tenant)
  // — evita mostrar instâncias de outros tenants que o superadmin possa ver na API
  const instanciasDosTenants = useMemo(() =>
    [...new Set(contatos.map(c => c.instancia).filter(Boolean))].sort(),
    [contatos]
  );

  const carregarGrupos = useCallback(async () => {
    try {
      const r = await api.get('/grupo/listar');
      setGrupos(r.data);
    } catch {}
  }, []);

  async function carregar() {
    try {
      const r = await api.get('/envio/contatos/listar');
      setContatos(r.data);
      setSelecionados(new Set());
    } finally {
      setPrimeiraCarga(false);
    }
  }

  useEffect(() => {
    carregar();
    carregarGrupos();
    // Instâncias registradas — para seletores nos formulários
    api.get('/whatsapp/instancias').then(r => {
      const lista = Array.isArray(r.data) ? r.data : [];
      const nomes = lista.map(i => i?.instance?.instanceName || i?.instanceName || i?.name).filter(Boolean);
      setInstanciasRegistradas(nomes);
    }).catch(() => {});
  }, [carregarGrupos]);

  // Todas as tags únicas presentes nos contatos — para as pílulas de filtro
  const todasAsTags = useMemo(() =>
    [...new Set(contatos.flatMap(c => Array.isArray(c.tags) ? c.tags : []))].sort(),
    [contatos]
  );

  // Filtros derivados — aplicados a contatos e grupos ao mesmo tempo
  const contatosFiltrados = contatos.filter(c => {
    if (filtroInstancia && c.instancia !== filtroInstancia) return false;
    if (filtroTag && !(Array.isArray(c.tags) && c.tags.includes(filtroTag))) return false;
    return true;
  });

  const gruposFiltrados = filtroInstancia
    ? grupos.filter(g => g.instancia === filtroInstancia)
    : grupos;

  // Contatos visíveis no auto-gerar (respeitando filtro ou instância explícita do form)
  const totalParaAutoGrupo = (() => {
    const inst = autoForm.instancia || filtroInstancia;
    return inst ? contatos.filter(c => c.instancia === inst).length : contatos.length;
  })();

  // Opções para seletores: une instâncias dos contatos + registradas (sem duplicatas)
  const instanciasParaSelect = useMemo(() =>
    [...new Set([...instanciasDosTenants, ...instanciasRegistradas])].sort(),
    [instanciasDosTenants, instanciasRegistradas]
  );

  // ── Contato handlers ──────────────────────────────────────────────

  // Converte string de tags digitada ("vip, lead") em array limpo ["vip", "lead"]
  function parseTags(str) {
    return (str || '').split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.telefone) { toast.error(t('contatos.toast_telefone_obrigatorio')); return; }
    try {
      await api.post('/envio/contato/adicionar', { ...form, tags: parseTags(form.tags) });
      toast.success(t('contatos.toast_adicionado'));
      setForm({ nome: '', telefone: '', email: '', tags: '' });
      setMostrarForm(false);
      carregar();
    } catch {
      toast.error(t('contatos.toast_erro_adicionar'));
    }
  }

  function iniciarEdicao(contato) {
    setEditando(contato.id);
    setFormEdit({
      nome: contato.nome || '',
      telefone: contato.telefone,
      email: contato.email || '',
      tags: Array.isArray(contato.tags) ? contato.tags.join(', ') : '',
    });
  }

  function cancelarEdicao() {
    setEditando(null);
    setFormEdit({ nome: '', telefone: '', email: '', tags: '' });
  }

  async function handleSalvarEdicao(id) {
    if (!formEdit.telefone) { toast.error(t('contatos.toast_telefone_obrigatorio')); return; }
    try {
      await api.put(`/envio/contato/${id}`, { ...formEdit, tags: parseTags(formEdit.tags) });
      toast.success(t('contatos.toast_atualizado'));
      cancelarEdicao();
      carregar();
    } catch {
      toast.error(t('contatos.toast_erro_atualizar'));
    }
  }

  async function handleDeletar(id, nome) {
    if (!window.confirm(t('contatos.confirm_remover', { nome: nome || id }))) return;
    try {
      await api.delete(`/envio/contato/${id}`);
      toast.success(t('contatos.toast_removido'));
      carregar();
    } catch {
      toast.error(t('contatos.toast_erro_remover'));
    }
  }

  async function handleToggleIA(c) {
    const novoValor = !c.ia_desativada;
    try {
      await api.patch(`/envio/contato/${c.id}/ia`, { ia_desativada: novoValor });
      toast.success(novoValor ? t('contatos.toast_ia_desativada') : t('contatos.toast_ia_ativada'));
      carregar();
    } catch {
      toast.error(t('contatos.toast_erro_ia'));
    }
  }

  async function handleLimparLids() {
    if (!window.confirm(t('contatos.confirm_limpar_lids'))) return;
    try {
      const { data } = await api.post('/envio/contatos/limpar-lids');
      if (data.removidos > 0) toast.success(t('contatos.toast_lids_removidos', { n: data.removidos }));
      else toast.success(t('contatos.toast_nenhum_invalido'));
      carregar();
    } catch {
      toast.error(t('contatos.toast_erro_limpar'));
    }
  }

  async function handleImportCSV(e) {
    const arquivo = e.target.files[0];
    if (!arquivo) return;
    setImportando(true);
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      if (instanciaImport) formData.append('instancia', instanciaImport);
      const { data } = await api.post('/importar/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: TIMEOUT_UPLOAD
      });
      // ⚠️ Toast VERDE com zero era o que o cliente via quando o CSV vinha do Excel
      // em português (ponto-e-vírgula) — ele subia o arquivo, lia "0 importados" em
      // verde e não tinha o que fazer. Hoje o backend recusa esse caso com 400 e
      // explicação; aqui fica a rede para os outros zeros possíveis.
      if (!data.importados) {
        toast.error(t('contatos.toast_csv_nenhum'));
      } else {
        toast.success(data.instancia
          ? t('contatos.toast_csv_importados_instancia', { n: data.importados, instancia: data.instancia })
          : t('nova_campanha.toast_csv_importados', { n: data.importados }));
        // `truncados` e `semTelefone` existiam na resposta e a tela IGNORAVA os dois —
        // quem estourava o teto do plano perdia contatos sem um aviso (lacuna já
        // registrada no CLAUDE.md em 09/10). Agora cada um tem o seu alerta.
        if (data.truncados > 0) {
          toast.error(t('contatos.toast_csv_truncados', { n: data.truncados, limite: data.limite }),
            { duration: 8000 });
        }
        // Contatos que já existiam DESATIVADOS e voltaram com este CSV. Avisar não é
        // opcional: o cliente apagou esses contatos em algum momento, e reativar em
        // silêncio ressuscitaria gente que ele tirou da base de propósito.
        if (data.reativados > 0) {
          toast(t('contatos.toast_csv_reativados', { n: data.reativados }),
            { icon: '♻️', duration: 8000 });
        }
        if (data.semTelefone > 0) {
          toast(t('contatos.toast_csv_sem_telefone', { n: data.semTelefone }), { icon: '⚠️', duration: 6000 });
        }
      }
      carregar();
    } catch (err) {
      // O backend retorna mensagem específica (ex.: limite de contatos atingido) em
      // err.response.data.erro. SEM `response` o erro nunca veio do backend — e era
      // esse o caso do incidente de 21/10: o axios abortava a importação nos 15s e a
      // tela dizia só "erro ao importar", indistinguível de um CSV mal formado. Um
      // timeout precisa dizer que é timeout, e avisar que os contatos PODEM ter
      // entrado — o servidor continua inserindo depois que o navegador desiste.
      if (err.response?.data?.erro) {
        toast.error(err.response.data.erro);
      } else if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        toast.error(t('contatos.toast_csv_timeout'), { duration: 10000 });
      } else if (!err.response) {
        toast.error(t('contatos.toast_csv_rede'), { duration: 8000 });
      } else {
        toast.error(t('nova_campanha.toast_csv_erro'));
      }
    } finally {
      setImportando(false);
      e.target.value = '';
    }
  }

  async function handleDeduplicate() {
    if (!window.confirm(t('contatos.confirm_dedup'))) return;
    setDeduplicando(true);
    try {
      const { data } = await api.post('/envio/contatos/deduplicate');
      if (data.merged > 0) toast.success(t('contatos.toast_dup_removidos', { n: data.merged }));
      else toast.success(t('contatos.toast_nenhum_dup'));
      carregar();
    } catch {
      toast.error(t('contatos.toast_erro_dedup'));
    } finally {
      setDeduplicando(false);
    }
  }

  function toggleSelecionado(id) {
    setSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelecionarTodos() {
    if (selecionados.size === contatosFiltrados.length && contatosFiltrados.length > 0) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(contatosFiltrados.map(c => c.id)));
    }
  }

  async function handleDeletarSelecionados() {
    if (selecionados.size === 0) return;
    if (!window.confirm(t('contatos.confirm_excluir_massa', { n: selecionados.size }))) return;
    setExcluindoEmMassa(true);
    const ids = Array.from(selecionados);
    const resultados = await Promise.allSettled(ids.map(id => api.delete(`/envio/contato/${id}`)));
    const falhas = resultados.filter(r => r.status === 'rejected').length;
    const sucessos = ids.length - falhas;
    if (sucessos > 0) toast.success(t('contatos.toast_excluidos', { n: sucessos }));
    if (falhas > 0) toast.error(t('contatos.toast_erro_excluir_massa', { n: falhas }));
    setExcluindoEmMassa(false);
    carregar();
  }

  // ── Grupo handlers ────────────────────────────────────────────────

  function abrirModalGrupo() {
    setFormGrupo({ nome: '', descricao: '', instancia: filtroInstancia || '' });
    setModalContatosBusca('');
    // Pré-seleciona os contatos já marcados na tabela
    setModalContatosSel(new Set(selecionados));
    setMostrarModalGrupo(true);
  }

  function toggleModalContato(id) {
    setModalContatosSel(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  async function handleCriarGrupo() {
    if (!formGrupo.nome) { toast.error(t('contatos.modal_nome_obrigatorio')); return; }
    setCriandoGrupo(true);
    try {
      const contato_ids = Array.from(modalContatosSel);
      await api.post('/grupo/criar', {
        nome: formGrupo.nome,
        descricao: formGrupo.descricao || null,
        instancia: formGrupo.instancia || null,
        contato_ids
      });
      toast.success(contato_ids.length > 0 ? t('contatos.modal_criado', { n: contato_ids.length }) : t('contatos.modal_criado_sem'));
      setMostrarModalGrupo(false);
      carregarGrupos();
    } catch {
      toast.error(t('contatos.modal_erro_criar'));
    } finally {
      setCriandoGrupo(false);
    }
  }

  async function abrirEditarGrupo(g) {
    if (grupoEditando?.id === g.id) { setGrupoEditando(null); return; }
    setGrupoEditando(g);
    setModalContatosBusca('');
    try {
      const { data } = await api.get(`/grupo/${g.id}/contatos`);
      setGrupoEditandoContatos(new Set(data.map(c => c.id)));
    } catch { setGrupoEditandoContatos(new Set()); }
  }

  function toggleEditContato(id) {
    setGrupoEditandoContatos(prev => {
      const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
    });
  }

  async function salvarEdicaoGrupo() {
    setSalvandoGrupoEdit(true);
    try {
      await api.post(`/grupo/${grupoEditando.id}/adicionar-contatos`, { contato_ids: Array.from(grupoEditandoContatos) });
      // Remove os que foram desmarcados
      const { data: atuais } = await api.get(`/grupo/${grupoEditando.id}/contatos`);
      const remover = atuais.map(c => c.id).filter(id => !grupoEditandoContatos.has(id));
      await Promise.all(remover.map(cid => api.delete(`/grupo/${grupoEditando.id}/contatos/${cid}`).catch(() => {})));
      toast.success(t('contatos.grupo_atualizado'));
      setGrupoEditando(null);
      carregarGrupos();
    } catch { toast.error(t('contatos.grupo_erro_salvar')); }
    finally { setSalvandoGrupoEdit(false); }
  }

  async function handleGerarAutomatico(e) {
    e.preventDefault();
    setGerandoAuto(true);
    try {
      const instancia = autoForm.instancia || filtroInstancia || null;
      const { data } = await api.post('/grupo/gerar-automatico', {
        tamanho: autoForm.tamanho,
        prefixo: autoForm.prefixo,
        instancia
      });
      toast.success(t('grupos.toast_gerados', { n: data.grupos_criados }));
      setMostrarAutoGrupo(false);
      carregarGrupos();
    } catch {
      toast.error(t('grupos.toast_erro_gerar'));
    } finally {
      setGerandoAuto(false);
    }
  }

  async function handleDeletarGrupo(id, nome) {
    if (!window.confirm(t('contatos.confirm_excluir_grupo', { nome }))) return;
    try {
      await api.delete(`/grupo/${id}`);
      toast.success(t('contatos.toast_grupo_excluido'));
      carregarGrupos();
    } catch {
      toast.error(t('contatos.toast_erro_excluir_grupo'));
    }
  }

  const todosSelecionados = contatosFiltrados.length > 0 && selecionados.size === contatosFiltrados.length;

  return (
    <div>
      {/* ── Header ── */}
      <div className="page-h">
        <div>
          <h1 className="page-h-title">
            {t('contatos.titulo')}
            <span className="muted" style={{ fontSize: '14px', fontWeight: 400, marginLeft: '8px' }}>
              ({contatosFiltrados.length}{filtroInstancia ? ` de ${contatos.length}` : ''})
            </span>
          </h1>
          <p className="page-h-sub">{t('contatos.pagina_sub')}</p>
        </div>
        <div className="page-h-actions">
          <button onClick={handleDeduplicate} disabled={deduplicando}
            title="Remove entradas duplicadas de um mesmo número de telefone"
            className="btn btn-secondary btn-sm">
            {deduplicando ? '⏳' : '🔁'} {t('contatos.btn_remover_dup')}
          </button>
          <button onClick={handleLimparLids}
            title="Remove contatos com telefone inválido (LIDs do WhatsApp)"
            className="btn btn-warning btn-sm">
            🧹 {t('contatos.btn_limpar_invalidos')}
          </button>
          {selecionados.size > 0 && (
            <button onClick={handleDeletarSelecionados} disabled={excluindoEmMassa}
              className="btn btn-danger">
              <Trash2 size={16} /> {t('contatos.btn_excluir_sel', { n: selecionados.size })}
            </button>
          )}
          {instanciasParaSelect.length > 0 && (
            // ⚠️ O RÓTULO não é enfeite: a tela já tem pílulas de FILTRO por instância
            // logo acima, então um select solto na barra lê como "mais um filtro".
            // Visto ao renderizar a barra; nenhum teste pegaria isso.
            <span className="muted" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              {t('contatos.import_instancia_label')}
            <select value={instanciaImport} onChange={e => setInstanciaImport(e.target.value)}
              disabled={importando} className="input input-sm"
              title={t('contatos.import_instancia_hint')}
              style={{ maxWidth: 190 }}>
              <option value="">{t('contatos.import_instancia_nenhuma')}</option>
              {instanciasParaSelect.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
            </span>
          )}
          <label className="btn btn-sm" title={t('nova_campanha.csv_hint')}
            style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac', cursor: importando ? 'wait' : 'pointer' }}>
            <Upload size={15} /> {importando ? t('nova_campanha.btn_importando') : t('nova_campanha.btn_importar_csv')}
            <input type="file" accept=".csv" onChange={handleImportCSV} disabled={importando} style={{ display: 'none' }} />
          </label>
          <button onClick={() => setMostrarForm(!mostrarForm)} className="btn btn-primary">
            <UserPlus size={16} /> {t('contatos.btn_novo')}
          </button>
        </div>
      </div>

      {/* ── Novo Contato ── */}
      {mostrarForm && (
        <div className="card" style={{ marginBottom: '1.5rem', maxWidth: '520px' }}>
          <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 1rem' }}>{t('contatos.form_titulo')}</p>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              [t('contatos.col_nome'), 'nome', 'text'],
              [t('contatos.col_telefone'), 'telefone', 'text'],
              [t('contatos.col_email'), 'email', 'email']
            ].map(([label, name, type]) => (
              <div key={name} style={{ gridColumn: name === 'email' ? 'span 2' : 'span 1' }}>
                <label className="label">
                  {label} {name === 'telefone' && <span className="field-required">*</span>}
                </label>
                <input type={type} value={form[name]}
                  onChange={e => setForm({ ...form, [name]: e.target.value })}
                  placeholder={name === 'telefone' ? t('contatos.placeholder_telefone') : ''}
                  className="input"
                />
              </div>
            ))}
            <div style={{ gridColumn: 'span 2' }}>
              <label className="label">{t('contatos.col_tags')} <span className="muted" style={{ fontWeight: 400 }}>({t('comum.opcional')})</span></label>
              <input type="text" value={form.tags}
                onChange={e => setForm({ ...form, tags: e.target.value })}
                placeholder={t('contatos.tags_placeholder')}
                className="input"
              />
              <p className="muted" style={{ fontSize: '11px', marginTop: 4 }}>{t('contatos.tags_hint')}</p>
            </div>
            <div style={{ gridColumn: 'span 2', display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{t('contatos.btn_salvar')}</button>
              <button type="button" onClick={() => setMostrarForm(false)} className="btn btn-secondary">{t('comum.cancelar')}</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Filtro por Instância ── (derivado dos contatos, sem mistura de outros tenants) */}
      {instanciasDosTenants.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
          <button onClick={() => { setFiltroInstancia(''); setSelecionados(new Set()); }}
            className={`pill${!filtroInstancia ? ' is-active' : ''}`}>
            {t('contatos.todas_instancias')}
            <span style={{ marginLeft: '5px', opacity: 0.7, fontSize: '11px' }}>({contatos.length})</span>
          </button>
          {instanciasDosTenants.map(inst => (
            <button key={inst} onClick={() => { setFiltroInstancia(inst); setSelecionados(new Set()); }}
              className={`pill${filtroInstancia === inst ? ' is-active' : ''}`}>
              {inst}
              <span style={{ marginLeft: '5px', opacity: 0.7, fontSize: '11px' }}>
                ({contatos.filter(c => c.instancia === inst).length})
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── Filtro por Tag ── */}
      {todasAsTags.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'center' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginRight: 2 }}>
            {t('contatos.col_tags')}:
          </span>
          <button onClick={() => setFiltroTag('')}
            className={`pill${!filtroTag ? ' is-active' : ''}`}
            style={!filtroTag ? { background: '#13305f', borderColor: '#13305f', color: '#fff' } : { borderColor: '#86efac', color: '#13305f' }}>
            {t('contatos.todas_tags')}
          </button>
          {todasAsTags.map(tag => (
            <button key={tag} onClick={() => setFiltroTag(filtroTag === tag ? '' : tag)}
              className={`pill${filtroTag === tag ? ' is-active' : ''}`}
              style={filtroTag === tag
                ? { background: '#13305f', borderColor: '#13305f', color: '#fff' }
                : { borderColor: '#86efac', color: '#13305f' }}>
              {tag}
              <span style={{ marginLeft: 4, opacity: 0.75, fontSize: '11px' }}>
                ({contatos.filter(c => Array.isArray(c.tags) && c.tags.includes(tag)).length})
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── Seção Grupos ── */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        {/* Header clicável */}
        <div
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setGruposAbertos(v => !v)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={16} color="var(--color-accent)" />
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{t('contatos.grupos_titulo')}</span>
            <span style={{ fontSize: '12px', background: 'var(--color-bg-soft)', border: '1px solid var(--color-border)', borderRadius: '10px', padding: '1px 7px', color: 'var(--color-text-muted)' }}>
              {gruposFiltrados.length}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={e => { e.stopPropagation(); setMostrarAutoGrupo(v => !v); setMostrarModalGrupo(false); setGruposAbertos(true); }}
              className="btn btn-sm"
              style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac' }}
            >
              <Zap size={13} /> {t('contatos.grupos_gerar_auto')}
            </button>
            {selecionados.size > 0 && (
              <button onClick={e => { e.stopPropagation(); abrirModalGrupo(); }} className="btn btn-secondary btn-sm">
                {t('contatos.grupos_com_sel', { n: selecionados.size })}
              </button>
            )}
            <button onClick={e => { e.stopPropagation(); abrirModalGrupo(); }} className="btn btn-primary btn-sm">
              <Plus size={14} /> {t('contatos.grupos_novo')}
            </button>
            {gruposAbertos ? <ChevronUp size={18} color="var(--color-text-muted)" /> : <ChevronDown size={18} color="var(--color-text-muted)" />}
          </div>
        </div>

        {gruposAbertos && (
          <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--color-divider)' }}>

            {/* Gerar automático — inline, com seletor de instância */}
            {mostrarAutoGrupo && (
              <div style={{ background: 'var(--color-bg-soft)', borderRadius: 'var(--radius)', border: '1px solid var(--color-border)', padding: '14px', margin: '12px 0' }}>
                <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>{t('contatos.grupos_auto_titulo')}</p>
                <p className="muted" style={{ fontSize: '12px', marginBottom: '12px' }}>
                  {t('contatos.grupos_auto_sub')}
                </p>
                <form onSubmit={handleGerarAutomatico} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label className="label">{t('contatos.grupos_instancia_label')}</label>
                    {instanciasParaSelect.length > 0 ? (
                      <select value={autoForm.instancia} onChange={e => setAutoForm({ ...autoForm, instancia: e.target.value })} className="select">
                        <option value="">{t('contatos.grupos_instancia_todas', { n: contatos.length })}</option>
                        {instanciasParaSelect.map(i => (
                          <option key={i} value={i}>{i} ({contatos.filter(c => c.instancia === i).length} contatos)</option>
                        ))}
                      </select>
                    ) : (
                      <input value={autoForm.instancia} onChange={e => setAutoForm({ ...autoForm, instancia: e.target.value })}
                        placeholder="Ex: disparador01 (deixe vazio para todas)" className="input" />
                    )}
                  </div>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
                      {t('contatos.grupos_tamanho_label')}
                      {ajuda.grupo_tamanho && <Tooltip texto={ajuda.grupo_tamanho.texto} exemplo={ajuda.grupo_tamanho.exemplo} />}
                    </label>
                    <input type="number" value={autoForm.tamanho} min={1}
                      onChange={e => setAutoForm({ ...autoForm, tamanho: parseInt(e.target.value) || 1 })}
                      className="input" />
                  </div>
                  <div>
                    <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
                      {t('contatos.grupos_prefixo_label')}
                      {ajuda.grupo_prefixo && <Tooltip texto={ajuda.grupo_prefixo.texto} exemplo={ajuda.grupo_prefixo.exemplo} />}
                    </label>
                    <input type="text" value={autoForm.prefixo}
                      onChange={e => setAutoForm({ ...autoForm, prefixo: e.target.value })}
                      className="input" />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <p className="muted" style={{ fontSize: '12px', marginBottom: '10px' }}>
                      {t('contatos.grupos_resultado', { grupos: Math.ceil(totalParaAutoGrupo / autoForm.tamanho), tam: autoForm.tamanho, total: totalParaAutoGrupo })}
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="submit" disabled={gerandoAuto || totalParaAutoGrupo === 0} className="btn btn-primary" style={{ flex: 1 }}>
                        {gerandoAuto ? t('contatos.grupos_gerando') : t('contatos.grupos_gerar_btn', { n: Math.ceil(totalParaAutoGrupo / autoForm.tamanho) })}
                      </button>
                      <button type="button" onClick={() => setMostrarAutoGrupo(false)} className="btn btn-secondary">{t('comum.cancelar')}</button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* Grid de grupos */}
            {gruposFiltrados.length === 0 ? (
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', textAlign: 'center', padding: '24px 0' }}>
                {filtroInstancia
                  ? t('contatos.grupos_vazio_instancia', { inst: filtroInstancia })
                  : t('contatos.grupos_vazio')}
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginTop: mostrarAutoGrupo ? 0 : '12px' }}>
                {gruposFiltrados.map(g => {
                  const editando = grupoEditando?.id === g.id;
                  const contatosVisiveis = contatos.filter(c => {
                    const busca = modalContatosBusca.trim().toLowerCase();
                    return !busca || (c.nome || '').toLowerCase().includes(busca) || (c.telefone || '').includes(busca);
                  });
                  return (
                    <div key={g.id} style={{
                      background: editando ? 'var(--color-accent-soft, #eff6ff)' : 'var(--color-bg-soft)',
                      borderRadius: 'var(--radius)',
                      border: `1px solid ${editando ? 'var(--color-accent)' : 'var(--color-border)'}`,
                      padding: '12px 14px',
                      display: 'flex', flexDirection: 'column', gap: '6px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.4, wordBreak: 'break-word' }}>{g.nome}</span>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button onClick={() => abrirEditarGrupo(g)} title="Editar contatos"
                            className="btn btn-icon btn-sm btn-secondary">
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => handleDeletarGrupo(g.id, g.nome)} title="Excluir grupo"
                            className="btn btn-icon btn-sm btn-danger">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      {g.instancia && <InstanciaBadge nome={g.instancia} />}
                      <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Users size={11} /> {g.total_contatos} contato(s)
                      </span>
                      {g.descricao && (
                        <span style={{ fontSize: '11px', color: 'var(--color-text-muted)', lineHeight: 1.4 }}>{g.descricao}</span>
                      )}
                      {editando && (
                        <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
                          <input
                            value={modalContatosBusca}
                            onChange={e => setModalContatosBusca(e.target.value)}
                            placeholder={t('contatos.buscar_contato')}
                            className="input"
                            style={{ marginBottom: 6, fontSize: 12 }}
                          />
                          <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, marginBottom: 8 }}>
                            {contatosVisiveis.length === 0 && (
                              <div style={{ padding: 10, fontSize: 12, color: 'var(--color-muted)' }}>{t('contatos.nenhum_contato')}</div>
                            )}
                            {contatosVisiveis.map(c => (
                              <div key={c.id} onClick={() => toggleEditContato(c.id)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '6px 10px', cursor: 'pointer',
                                  borderBottom: '1px solid var(--color-divider)',
                                  background: grupoEditandoContatos.has(c.id) ? 'var(--color-accent-soft)' : 'transparent',
                                  fontSize: 12
                                }}>
                                <input type="checkbox" readOnly checked={grupoEditandoContatos.has(c.id)} style={{ accentColor: 'var(--color-accent)' }} />
                                <span>{c.nome || '—'} · {c.telefone}</span>
                              </div>
                            ))}
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={salvarEdicaoGrupo} disabled={salvandoGrupoEdit} className="btn btn-primary btn-sm" style={{ flex: 1 }}>
                              {salvandoGrupoEdit ? t('contatos.grupo_salvando') : t('contatos.grupo_salvar', { n: grupoEditandoContatos.size })}
                            </button>
                            <button onClick={() => setGrupoEditando(null)} className="btn btn-secondary btn-sm">{t('comum.cancelar')}</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modal Novo Grupo — position:fixed, não precisa expandir a seção ── */}
      {mostrarModalGrupo && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--color-border)', padding: '24px', width: '100%', maxWidth: '440px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{t('contatos.modal_grupo_titulo')}</h3>
              <button onClick={() => setMostrarModalGrupo(false)} className="btn btn-icon btn-sm btn-secondary">
                <X size={16} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label className="label">{t('contatos.modal_nome_label')} <span className="field-required">*</span></label>
                <input value={formGrupo.nome} onChange={e => setFormGrupo({ ...formGrupo, nome: e.target.value })}
                  placeholder={t('contatos.modal_nome_placeholder')} className="input" autoFocus />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="label">{t('contatos.modal_descricao_label')} <span className="muted">({t('comum.opcional')})</span></label>
                  <input value={formGrupo.descricao} onChange={e => setFormGrupo({ ...formGrupo, descricao: e.target.value })}
                    placeholder={t('contatos.modal_descricao_placeholder')} className="input" />
                </div>
                <div>
                  <label className="label">{t('contatos.modal_instancia_label')} <span className="muted">({t('comum.opcional')})</span></label>
                  {instanciasParaSelect.length > 0 ? (
                    <select value={formGrupo.instancia} onChange={e => setFormGrupo({ ...formGrupo, instancia: e.target.value })} className="select">
                      <option value="">{t('contatos.modal_instancia_todas')}</option>
                      {instanciasParaSelect.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  ) : (
                    <input value={formGrupo.instancia} onChange={e => setFormGrupo({ ...formGrupo, instancia: e.target.value })}
                      placeholder="Ex: disparador01" className="input" />
                  )}
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label className="label" style={{ margin: 0 }}>{t('contatos.modal_contatos_label', { n: modalContatosSel.size })}</label>
                  <input value={modalContatosBusca} onChange={e => setModalContatosBusca(e.target.value)}
                    placeholder={t('contatos.modal_buscar')} className="input" style={{ width: 160, fontSize: 12, padding: '4px 8px' }} />
                </div>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
                  {contatos.filter(c => {
                    const busca = modalContatosBusca.trim().toLowerCase();
                    const instOk = !formGrupo.instancia || c.instancia === formGrupo.instancia || !c.instancia;
                    const textoOk = !busca || (c.nome || '').toLowerCase().includes(busca) || (c.telefone || '').includes(busca);
                    return instOk && textoOk;
                  }).map(c => (
                    <div key={c.id} onClick={() => toggleModalContato(c.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                        cursor: 'pointer', borderBottom: '1px solid var(--color-divider)', fontSize: 13,
                        background: modalContatosSel.has(c.id) ? 'var(--color-accent-soft)' : 'transparent'
                      }}>
                      <input type="checkbox" readOnly checked={modalContatosSel.has(c.id)} style={{ accentColor: 'var(--color-accent)' }} />
                      <span>{c.nome || '—'} · {c.telefone}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                <button onClick={handleCriarGrupo} disabled={criandoGrupo} className="btn btn-primary" style={{ flex: 1 }}>
                  {criandoGrupo ? t('comum.processando') : (modalContatosSel.size > 0 ? t('contatos.modal_criar_btn', { n: modalContatosSel.size }) : t('contatos.modal_criar_btn_sem'))}
                </button>
                <button onClick={() => setMostrarModalGrupo(false)} className="btn btn-secondary">{t('comum.cancelar')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Tabela de Contatos ── */}
      <div className="card card-flush">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  checked={todosSelecionados}
                  ref={el => { if (el) el.indeterminate = selecionados.size > 0 && !todosSelecionados; }}
                  onChange={toggleSelecionarTodos}
                  style={{ cursor: 'pointer', accentColor: 'var(--color-accent)' }}
                  title={t('contatos.selecionar_todos')}
                />
              </th>
              {[t('contatos.col_nome'), t('contatos.col_telefone'), t('contatos.col_email'), t('contatos.col_instancia'), t('contatos.col_tags'), t('contatos.col_ia'), t('contatos.col_cadastrado'), t('contatos.col_acoes')].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {primeiraCarga && <SkeletonTableRows rows={5} cols={9} />}
            {!primeiraCarga && contatosFiltrados.map(c => (
              <tr key={c.id} style={{ background: selecionados.has(c.id) ? 'var(--color-accent-soft)' : 'transparent' }}>
                <td>
                  <input type="checkbox" checked={selecionados.has(c.id)} onChange={() => toggleSelecionado(c.id)}
                    disabled={editando === c.id}
                    style={{ cursor: editando === c.id ? 'not-allowed' : 'pointer', accentColor: 'var(--color-accent)' }}
                  />
                </td>
                {editando === c.id ? (
                  <>
                    <td><input value={formEdit.nome} onChange={e => setFormEdit({ ...formEdit, nome: e.target.value })} placeholder="Nome" className="input input-sm" /></td>
                    <td><input value={formEdit.telefone} onChange={e => setFormEdit({ ...formEdit, telefone: e.target.value })} placeholder="Telefone" className="input input-sm" /></td>
                    <td><input value={formEdit.email} onChange={e => setFormEdit({ ...formEdit, email: e.target.value })} placeholder="Email" type="email" className="input input-sm" /></td>
                    <td className="table-cell-muted" style={{ fontSize: '12px' }}>{c.instancia || '—'}</td>
                    <td>
                      <input value={formEdit.tags} onChange={e => setFormEdit({ ...formEdit, tags: e.target.value })}
                        placeholder={t('contatos.tags_placeholder')} className="input input-sm"
                        title={t('contatos.tags_hint')} style={{ minWidth: 100 }} />
                    </td>
                    <td className="table-cell-muted" style={{ textAlign: 'center' }}>—</td>
                    <td className="table-cell-muted">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="row-tight">
                        <button onClick={() => handleSalvarEdicao(c.id)} title="Salvar" className="btn btn-icon btn-sm" style={{ background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac' }}><Check size={13} /></button>
                        <button onClick={cancelarEdicao} title="Cancelar" className="btn btn-icon btn-sm btn-secondary"><X size={13} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ fontWeight: 500 }}>{c.nome || '—'}</td>
                    <td className="table-cell-muted">{c.telefone}</td>
                    <td className="table-cell-muted">{c.email || '—'}</td>
                    <td><InstanciaBadge nome={c.instancia} /></td>
                    <td>
                      {Array.isArray(c.tags) && c.tags.length > 0
                        ? c.tags.map(tag => (
                            <span key={tag} onClick={() => setFiltroTag(filtroTag === tag ? '' : tag)}
                              style={{
                                display: 'inline-block', fontSize: '11px', cursor: 'pointer',
                                background: filtroTag === tag ? '#13305f' : '#dcfce7',
                                color: filtroTag === tag ? '#fff' : '#13305f',
                                borderRadius: '4px', padding: '1px 6px', marginRight: 3, marginBottom: 2,
                                whiteSpace: 'nowrap',
                              }}>
                              {tag}
                            </span>
                          ))
                        : <span className="table-cell-muted">—</span>}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleToggleIA(c)}
                        title={c.ia_desativada ? t('contatos.ia_ativar_title') : t('contatos.ia_desativar_title')}
                        className="btn btn-icon btn-sm"
                        style={c.ia_desativada
                          ? { background: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }
                          : { background: '#f0fdf4', color: 'var(--color-success)', borderColor: '#86efac' }}
                      >
                        {c.ia_desativada ? <BotOff size={13} /> : <Bot size={13} />}
                      </button>
                    </td>
                    <td className="table-cell-muted">{new Date(c.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="row-tight">
                        <button onClick={() => iniciarEdicao(c)} title="Editar" className="btn btn-icon btn-sm btn-secondary"><Pencil size={13} /></button>
                        <button onClick={() => handleDeletar(c.id, c.nome)} title="Remover" className="btn btn-icon btn-sm btn-danger"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {!primeiraCarga && contatosFiltrados.length === 0 && (
              <tr><td colSpan={9}>
                <div className="empty">
                  {filtroInstancia
                    ? t('contatos.vazio_instancia', { inst: filtroInstancia })
                    : t('contatos.vazio')}
                </div>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
